import { useCallback, useId, useState } from "react";
import { Activity, AlertCircle, CheckCircle2, CircleHelp, TriangleAlert } from "lucide-react";
import styled from "styled-components";
import { formatAnomaliesDate, getOverallTrafficStatus, getTrafficComparisonPeriod, getTrafficProblems, type AnomaliesScope, type OverviewAnomalies } from "./overviewAnomalies";
import { useOverviewAnomalies } from "./useOverviewAnomalies";
import { OverviewAnomaliesDetails, OverviewAnomaliesPopup } from "./OverviewAnomaliesPopup";
import { useTranslation } from "react-i18next";
import { getAppLanguage } from "src/i18n";
import { useRouter } from "src/services/Router";
import { StatCardHeader } from "./StatCardHeader";

const tones = {
	ok: { color: "#157347", background: "#f0faf4", icon: CheckCircle2 },
	warning: { color: "#8a5700", background: "#fff9ed", icon: TriangleAlert },
	critical: { color: "#b42332", background: "#fff3f3", icon: AlertCircle },
	unavailable: { color: "#667085", background: "#f6f7fa", icon: CircleHelp },
};

const Panel = styled.section`
	display: flex; flex-direction: column; min-width: 0; overflow: hidden;
	border: 1px solid #e1e6ef; border-radius: 12px; background: #fff; color: #344054; container-type: inline-size;
`;
const InlineDetails = styled.div`
	min-width: 0;
`;

export function OverviewAnomaliesCard({ scope }: { scope: AnomaliesScope }) {
	const { t } = useTranslation();
	const language = getAppLanguage();
	const router = useRouter();
	const detailsId = useId();
	const { busy, changes, result, error } = useOverviewAnomalies(scope);
	const contextKey = JSON.stringify([scope.userId, scope.projectId, scope.targetType, scope.targetId]);
	const [opened, setOpened] = useState<{ key: string; result: OverviewAnomalies; mode: "inline" | "popup" } | null>(null);
	const close = useCallback(() => setOpened(null), []);
	const visibleChanges = busy || error ? null : changes;
	const metadata = busy || error ? undefined : result;
	const status = getOverallTrafficStatus(visibleChanges);
	const toneLabels = { ok: t("traffic.normal"), warning: t("traffic.warning"), critical: t("traffic.critical"), unavailable: t("traffic.unavailable") };
	const tone = { ...tones[status], label: toneLabels[status] };
	const Icon = busy ? Activity : error ? CircleHelp : tone.icon;
	const describeChange = (change: NonNullable<typeof visibleChanges>[number]) => {
		if (change.fromZero) return t("traffic.riseFromZero");
		if (change.changePercent === null || !Number.isFinite(change.changePercent)) return t("traffic.noComparison");
		if (change.changePercent === 0) return t("traffic.noChange");
		return t(change.changePercent > 0 ? "traffic.increase" : "traffic.decrease", {
			percent: Math.abs(change.changePercent).toLocaleString(language === "ru" ? "ru-RU" : "en-GB", { maximumFractionDigits: 2 }),
		});
	};
	const localizedSummary = () => {
		const problems = (visibleChanges ?? []).filter(change => ["critical", "warning"].includes(getOverallTrafficStatus([change])));
		const total = problems.find(change => change.id === "total");
		const clients = problems.filter(change => change.id !== "total");
		if (problems.length === 1) return `${problems[0].label}: ${describeChange(problems[0])}`;
		if (problems.length) return [total ? `${total.label}: ${describeChange(total)}` : null, clients.length ? t("traffic.clientsWithDeviations", { count: clients.length }) : null].filter(Boolean).join(" · ");
		return status === "ok" ? t("traffic.noDailyDeviations") : t("traffic.insufficientData");
	};
	const description = busy ? t("traffic.checkingTraffic") : error ? (language === "ru" ? error : t("traffic.requestError")) : localizedSummary();
	const period = metadata ? getTrafficComparisonPeriod(metadata) : null;
	const summary = period ? `${formatAnomaliesDate(period.bucket)} · ${description}` : description;
	const problemCount = metadata ? getTrafficProblems(metadata.changes).length : 0;
	const opensPopup = problemCount > 7;
	const currentView = opened?.key === contextKey && opened.result === metadata ? opened : null;
	const inlineExpanded = currentView?.mode === "inline";

	return <>
		<Panel aria-label={t("traffic.controlDay")} aria-busy={busy}>
			<StatCardHeader title={t("traffic.controlDay")} label={t("traffic.openDetails")} disabled={!metadata} opensPopup={opensPopup}
				expanded={metadata && !opensPopup ? inlineExpanded : undefined} controls={metadata && !opensPopup ? detailsId : undefined}
				icon={<Icon size={20} color={tone.color} aria-hidden="true" />} description={summary} descriptionTitle={summary}
				badge={{ ...tone, label: busy ? t("traffic.checking") : error ? t("traffic.loadError") : tone.label }}
				onToggle={() => { if (metadata) setOpened(inlineExpanded ? null : { key: contextKey, result: metadata, mode: opensPopup ? "popup" : "inline" }); }} />
			{inlineExpanded && metadata && <InlineDetails id={detailsId}><OverviewAnomaliesDetails result={metadata} onClientNavigate={clientId => router.navigate("client", { cid: String(clientId) })} /></InlineDetails>}
		</Panel>
		{currentView?.mode === "popup" && metadata && <OverviewAnomaliesPopup result={metadata} onClose={close} />}
	</>;
}
